// Coaching Dashboard Types
// Note: These are frontend types that match the API response format.
// The backend uses Problem model but transforms data for backwards compatibility.

import type React from 'react'
import type {
  CoachingClientStatus,
  CoachingHomeworkStatus,
  ProblemRelationshipType,
  ProblemStatus,
  ProblemType,
  ProblemVisibility,
  RelationshipStrength,
} from '../../../db/schema/enums.js'
import type {
  ApproachCodeBlock,
  ApproachComplexityBlock,
  ApproachContentBlock,
  EditorialApproachBlock,
  EditorialBlock,
  MarkdownBlock,
} from '../../../db/schema/problems.js'
import { assertNever } from '../../../lib/assert-never.js'
import type { ProblemTopic } from '../../../lib/problems/problem-topics.js'

// Re-export editorial block types from schema (single source of truth)
export type {
  ApproachCodeBlock,
  ApproachComplexityBlock,
  ApproachContentBlock,
  EditorialApproachBlock,
  EditorialBlock,
  MarkdownBlock,
}

export type {
  CoachingClientStatus,
  CoachingHomeworkStatus,
  ProblemStatus,
  ProblemType,
  ProblemVisibility,
}

// Difficulty level object from DifficultyLevel table
export interface DifficultyInfo {
  id: string
  name: string
  level: number
  color: string | null
}
export type CoachingHomeworkType =
  | 'PROBLEM'
  | 'EXERCISE'
  | 'TASK'
  | 'SYSTEM_DESIGN'
  | 'SOURCE_CONTENT'
  | 'OTHER'

// Notion template mapping - maps homework type + source to Notion templates
export interface NotionTemplate {
  name: string // Friendly name (e.g., "LeetCode Problem")
  templateId: string // 32-char Notion template ID
  source: string | null // Source name to match (e.g., "LeetCode", "Beyond Cracking the Coding Interview")
  homeworkType: CoachingHomeworkType | null // Homework type to match (e.g., "PROBLEM", "SOURCE_CONTENT")
}

// Homework type options for template mapping dropdown
export const HOMEWORK_TYPE_OPTIONS = [
  { value: 'PROBLEM', label: 'Problem' },
  { value: 'EXERCISE', label: 'Exercise' },
  { value: 'SYSTEM_DESIGN', label: 'System Design' },
  { value: 'TASK', label: 'Task' },
  { value: 'SOURCE_CONTENT', label: 'Source Content' },
  { value: 'OTHER', label: 'Other' },
] as const

export interface CoachingClientActivityLogEntry {
  id: string
  clientId: string
  checkedInAt: Date | string
  createdAt: Date | string
}

export interface CoachingClient {
  id: string
  name: string
  /** Server-owned presentation selector; absent legacy DTOs default Classic. */
  dashboardExperience?: 'classic' | 'v2'
  namePronunciation?: string | null
  email?: string | null
  phone?: string | null
  address?: string | null
  location?: string | null
  timeZone?: string | null
  programmingLanguage?: string | null
  discordUsername?: string | null
  interviewingIoUsername?: string | null
  githubProfile?: string | null
  leetcodeProfile?: string | null
  linkedinProfile?: string | null
  dropboxRecordingsLink?: string | null
  notionDatabaseId?: string | null
  notionTemplates?: NotionTemplate[] | null
  targetRoles?: string | null
  availabilityNotes?: string | null
  notes?: string | null
  baselineEpochAt?: string | null
  baselineEpochSetAt?: string | null
  baselineEpochSetBy?: string | null
  baselineEpochUpdatedAt?: string | null
  baselineEpochClearedAt?: string | null
  baselineEpochClearedBy?: string | null
  status: CoachingClientStatus
  createdAt: string
  updatedAt: string
  // Every coaching client is a platform user.
  userId: string
  questionHistory?: CoachingQuestionHistoryWithQuestion[]
  assignedHomework?: CoachingHomeworkWithProblem[]
  activityLog?: CoachingClientActivityLogEntry[]
  lastCheckInAt?: string | null
}

// Simplified source info for search and display
export interface ProblemSourceSummary {
  id: string
  sourceId: string
  source: string // Source name
  sourceIcon?: string | null
  slug: string
  title: string
  externalId?: string | null
  order: number
}

/** Same question from another source — linked via ProblemVariant kind='duplicate' */
export interface ProblemDuplicateLink {
  problemId: string
  slug: string | null
  title: string | null
  sourceName: string
}

/** Additive fields on admin questions.getById beyond CoachingQuestion */
export interface AdminQuestionDetail {
  duplicates: ProblemDuplicateLink[]
}

// CoachingQuestion is now backed by the Problem model
// The API transforms Problem data to this format for backwards compatibility
export interface CoachingQuestion {
  id: string
  externalId?: string | null
  name: string // Maps to Problem.title
  slug?: string | null // Problem slug
  difficulty: DifficultyInfo | null // Difficulty level from DifficultyLevel table
  premiumRequired: boolean
  type: ProblemType
  problemTags: { slug: string; name: string }[]
  acceptanceRate?: number | null
  freqBar?: number | null
  totalAccepted?: number | null
  totalSubmitted?: number | null
  companies?: string[] // Company slugs
  url?: string | null
  editorialUrl?: string | null
  source: string
  sourceIcon?: string | null
  allSources?: ProblemSourceSummary[] | null // All sources for search/display
  // Publishing & Access Control
  status?: ProblemStatus
  visibility?: ProblemVisibility
  approaches?: QuestionApproach[] | null
  studyPlans?: StudyPlanSummary[] | null
  createdAt: Date | string
  updatedAt: Date | string
}

// Study plan summary for display in problem table
export interface StudyPlanSummary {
  id: string
  name: string
  sourceIcon?: string | null
}

// Subtopic option for approach tagging (grouped by topic in pickers).
// NOTE: interface name kept as ApproachStrategy to limit churn during the
// taxonomy migration — rename to ApproachSubtopic in a follow-up.
export interface ApproachStrategy {
  id: string
  name: string
  slug: string
  topicName?: string
}

// Variable definition for complexity entries (e.g., "n = number of nodes")
export interface VariableDefinition {
  variable: string
  definition: string
}

// Solution complexity entry for per-function complexity tracking
export interface SolutionComplexityEntry {
  id?: string
  functionName: string
  timeComplexity?: string | null
  timeComplexityExplanation?: string | null
  spaceComplexity?: string | null
  spaceComplexityExplanation?: string | null
  variableDefinitions?: VariableDefinition[]
  overridesApproach?: boolean
  order: number
}

// Approach-level complexity entry (language-agnostic)
export interface ApproachComplexityEntry {
  id?: string
  functionName: string
  timeComplexity?: string | null
  timeComplexityExplanation?: string | null
  spaceComplexity?: string | null
  spaceComplexityExplanation?: string | null
  variableDefinitions?: VariableDefinition[]
  order: number
}

// Solution code for an approach in a specific language
export interface ApproachSolution {
  id?: string
  language: string // 'python' | 'javascript' | 'java' | 'cpp' | 'go'
  code: string
  explanation?: string | null
  complexities?: SolutionComplexityEntry[]
}

// Supported languages for approach solutions
export const SUPPORTED_SOLUTION_LANGUAGES = [
  { value: 'python', label: 'Python', monacoId: 'python' },
  { value: 'javascript', label: 'JavaScript', monacoId: 'javascript' },
  { value: 'java', label: 'Java', monacoId: 'java' },
  { value: 'cpp', label: 'C++', monacoId: 'cpp' },
  { value: 'go', label: 'Go', monacoId: 'go' },
] as const

export type SolutionLanguage =
  (typeof SUPPORTED_SOLUTION_LANGUAGES)[number]['value']

// Structured approach (from Approach model)
export interface QuestionApproach {
  id?: string
  /** Temporary client-side ID for tracking unsaved approaches */
  tempId?: string
  name?: string
  solutionType?: 'brute_force' | 'acceptable' | 'optimal' | null
  blocks?: ApproachContentBlock[]
  tags?: string[]
  strategies?: { id: string; name: string }[] // Strategies associated with this approach
  solutions?: ApproachSolution[] // Code solutions in different languages
  complexities?: ApproachComplexityEntry[]
  isOptimal?: boolean
  isPreferred?: boolean
  order?: number
  difficulty?: DifficultyInfo | null // Difficulty level for this approach
}

// Problem follow-up (constraint modification challenges)
export interface ProblemFollowUp {
  id?: string
  /** Temporary client-side ID for tracking unsaved follow-ups */
  tempId?: string
  prompt: string
  order: number
  expectedTimeComplexity?: string | null
  expectedSpaceComplexity?: string | null
  prereqs: string[] // IDs of prerequisite follow-ups
  approachIds: string[] // IDs of approaches that satisfy this follow-up
}

// Problem relationship types (inter-problem connections)
// prerequisite: Understanding target helps before attempting source
// builds_on: Source's solution extends target's solution
// harder_than: Source is target with harder constraints
// The DB enum's own value type, not a copy: a value added there fails the
// switch in getRelationshipSentence at compile time (LG-484).
export type { ProblemRelationshipType }

// Relationship strength levels
// required: Must complete first (blocks progress)
// recommended: Should complete first (suggestion)
// optional: Nice-to-have (bonus)
// Likewise the DB enum's own value type — the STRENGTH_BADGES record in the
// dependencies section is keyed by it, so a grown enum fails there at tsc.
export type { RelationshipStrength }

// Problem relationship (inter-problem dependency edge)
export interface ProblemRelationship {
  id: string
  type: ProblemRelationshipType
  strength: RelationshipStrength
  notes: string | null
  direction: 'outgoing' | 'incoming'
  sourceProblemId: string
  targetProblemId: string
  relatedProblem: {
    id: string
    title: string
    externalId: string | null
  }
  createdAt: string
  updatedAt: string
}

// Relationship type metadata for display
export interface RelationshipTypeInfo {
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  description: string
}

// Strength metadata for display
export interface StrengthBadgeInfo {
  label: string
  color: string
}

/**
 * Generate a human-readable sentence describing a relationship.
 * Arrows always go left-to-right: [dependency] → [dependent]
 *
 * @param type - The relationship type
 * @param sourceName - The name of the source problem (the one that depends on / builds on / is harder than)
 * @param targetName - The name of the target problem (the one being depended on)
 * @param direction - Whether this is outgoing (source is current) or incoming (target is current)
 * @param currentProblemName - The name of the current problem being edited
 */
export function getRelationshipSentence(
  type: ProblemRelationshipType,
  sourceName: string,
  targetName: string,
  direction: 'outgoing' | 'incoming',
  currentProblemName: string,
): { main: string; clarification: string } {
  // For all types, the source depends on / builds on / is harder than the target
  // Arrow goes: target → source (target is the foundation, source depends on it)
  const isCurrentSource = direction === 'outgoing'

  switch (type) {
    case 'prerequisite':
      // Source requires understanding Target first
      // target → source (target is prereq for source)
      return {
        main: `${targetName} → ${sourceName}`,
        clarification: isCurrentSource
          ? `Understanding "${targetName}" helps before attempting "${currentProblemName}"`
          : `Understanding "${currentProblemName}" helps before attempting "${sourceName}"`,
      }
    case 'builds_on':
      // Source's solution extends Target's solution
      // target → source (source builds on target)
      return {
        main: `${targetName} → ${sourceName}`,
        clarification: isCurrentSource
          ? `"${currentProblemName}" extends the solution from "${targetName}"`
          : `"${sourceName}" extends the solution from "${currentProblemName}"`,
      }
    case 'harder_than':
      // Source is Target with harder constraints
      // target → source (source is harder version of target)
      return {
        main: `${targetName} → ${sourceName}`,
        clarification: isCurrentSource
          ? `"${currentProblemName}" is "${targetName}" with harder constraints`
          : `"${sourceName}" is "${currentProblemName}" with harder constraints`,
      }
    default:
      return assertNever(type, 'Unexpected relationship type')
  }
}

export interface CoachingQuestionHistory {
  id: string
  clientId: string
  questionId: string // Legacy field name (actually problemId)
  problemId?: string // New field name
  doneBefore: boolean
  lastAttemptDate?: Date | string | null
}

/** Narrow question view attached to a client's question history — NOT the full CoachingQuestion (no createdAt/updatedAt/slug/etc; matches the coaching clients.list router's nested projection). */
export interface CoachingQuestionHistoryEntryQuestion {
  id: string
  externalId?: string | null
  name: string
  difficulty: DifficultyInfo | null
  premiumRequired: boolean
  topics: ProblemTopic[]
  url?: string | null
  source: string
  approaches?: QuestionApproach[]
}

export interface CoachingQuestionHistoryWithQuestion
  extends CoachingQuestionHistory {
  question: CoachingQuestionHistoryEntryQuestion | null
}

export interface CoachingHomework {
  id: string
  clientId: string
  type: CoachingHomeworkType
  problemId?: string | null
  exerciseId?: string | null
  systemDesignProblemId?: string | null
  taskId?: string | null
  sourceContentId?: string | null
  title?: string | null
  description?: string | null
  url?: string | null
  notes?: string | null
  notionPageId?: string | null
  notionTemplateId?: string | null // Override for automatic template selection
  dateAssigned: Date | string
  status: CoachingHomeworkStatus
}

export interface CoachingHomeworkWithProblem extends CoachingHomework {
  problem?: {
    id: string
    externalId?: string | null
    name: string
    difficulty: DifficultyInfo | null
    premiumRequired: boolean
    topics: ProblemTopic[]
    url?: string | null
    source: string
    sourceIcon?: string | null
    approaches?: QuestionApproach[]
  } | null
  systemDesignProblem?: {
    id: string
    title: string
    difficulty: DifficultyInfo | null
    url?: string | null
    source: string
    sourceIcon?: string | null
  } | null
  sourceContent?: {
    id: string
    title: string
    slug: string
    kind: string
    description?: string | null
    url?: string | null
    estimatedMinutes?: number | null
    sourceName?: string
    sourceIcon?: string | null
  } | null
  task?: {
    id: string
    title: string
    resourceId?: string | null
  } | null
}

// Form data types
// Problem source (from ProblemSource table, joined with Source)
export interface ProblemSource {
  id: string
  sourceId: string // FK to Source table
  source: string // Source.name (joined)
  sourceIcon?: string | null // Source.icon (joined)
  /**
   * `Source.slug`, or null when the source was soft-deleted. Required for the
   * same reason as `isOriginal`: the transformer always emits it, and
   * `buildAttribution` reads null as "source is gone" and drops the row
   * entirely — so a producer that merely *omitted* it would type-check and
   * make that source vanish from the widget with no error.
   */
  sourceSlug: string | null // Source.slug (joined) — identifies BCTCI
  sourceSiteUrl?: string | null // Source.url (joined) — site-level fallback
  slug: string // URL-friendly identifier, unique per source
  title: string // The source's name for this problem (required)
  externalId?: string | null
  difficulty?: DifficultyInfo | null
  premiumRequired: boolean
  url?: string | null
  editorialUrl?: string | null
  /**
   * Whether this is the source the problem originated on (at most one).
   *
   * Required, not optional: the column is `NOT NULL` and
   * `transformSourceForResponse` always emits it, so an optional would invent
   * an "absent" state no producer can create and make every reader collapse it.
   */
  isOriginal: boolean
  order: number
  createdAt?: Date | string
  updatedAt?: Date | string
}

// Default sources for quick-add functionality (legacy - prefer using Source table)
export const DEFAULT_SOURCES = [
  { name: 'LeetCode', icon: 'leetcode.png' },
  { name: 'Beyond Cracking the Coding Interview', icon: 'bctci.png' },
  { name: 'Cracking the Coding Interview', icon: null },
  { name: 'NeetCode', icon: 'neetcode.png' },
  { name: 'HackerRank', icon: 'hackerrank.png' },
  { name: 'AlgoExpert', icon: null },
  { name: 'CodeSignal', icon: null },
  { name: 'InterviewBit', icon: null },
] as const

// Source category types
export type SourceCategory = 'prep_resource'

export const SOURCE_CATEGORY_LABELS: Record<SourceCategory, string> = {
  prep_resource: 'Interview Prep Resource',
}

// Normalized source (from Source table)
export interface Source {
  id: string
  name: string
  slug: string
  icon: string | null
  category: SourceCategory
  url: string | null
  createdAt: string
  updatedAt: string
}

export interface ClientFormData {
  name: string
  /** The platform user this client is linked to (LG-982: required to create). */
  userId?: string
  namePronunciation?: string
  email?: string
  phone?: string
  address?: string
  location?: string
  timeZone?: string
  programmingLanguage?: string
  discordUsername?: string
  interviewingIoUsername?: string
  githubProfile?: string
  leetcodeProfile?: string
  linkedinProfile?: string
  dropboxRecordingsLink?: string
  notionDatabaseId?: string
  notionTemplates?: NotionTemplate[]
  targetRoles?: string
  availabilityNotes?: string
  notes?: string
  status?: string
}
